import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Flame, Droplet, Heart, Loader2, ShieldAlert, Check, PlayCircle, Plus, History, Timer, Shuffle } from 'lucide-react';
import { Card, Btn, Empty } from './shared';
import CompleteWorkoutSheet from '../CompleteWorkoutSheet';
import SwapSheet from './SwapSheet';
import InjuryReviewSheet from './InjuryReviewSheet';
import ShojinStrip from './ShojinStrip';
import ContextualNudgeBanner from './ContextualNudgeBanner';
import SafetyAdaptedPanel from './SafetyAdaptedPanel';
import { youtubeVideoUrl, hasCuratedVideo } from '../../lib/exerciseVideos';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

// Inline rest timer (sticky between sets). Defaults to 90s; user picks 60/90/2m/3m/5m.
const RestTimer = ({ exIdx }) => {
  const [target, setTarget] = useState(90);
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(ref.current); setRunning(false); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running]);

  const start = () => { setRemaining(target); setRunning(true); };
  const stop = () => { setRunning(false); setRemaining(0); };

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const done = running && remaining === 0;

  return (
    <div
      data-testid={`daily-rest-timer-${exIdx}`}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-[11px] font-bold ${
        done ? 'border-[#BEFF38] bg-[#BEFF38]/10 text-[#BEFF38]' :
        running ? 'border-[#ff7a1a] bg-[#ff7a1a]/10 text-white' :
        'border-[#2a2f3a] bg-[#0b0c10] text-zinc-300'
      }`}
    >
      <Timer className="w-3.5 h-3.5" />
      <span className="font-mono tabular-nums">
        {running ? `${mm}:${ss}` : `${target}s rest`}
      </span>
      <select
        value={target}
        onChange={(e) => setTarget(Number(e.target.value))}
        disabled={running}
        data-testid={`daily-rest-target-${exIdx}`}
        className="ml-auto bg-[#0f1115] border border-[#2a2f3a] rounded text-zinc-300 text-[10px] px-1 py-0.5 outline-none disabled:opacity-50"
      >
        <option value="60">60s</option>
        <option value="90">90s</option>
        <option value="120">2m</option>
        <option value="180">3m</option>
        <option value="300">5m</option>
      </select>
      {!running ? (
        <button onClick={start} data-testid={`daily-rest-start-${exIdx}`} className="text-[#BEFF38] uppercase tracking-wider text-[10px]">Start</button>
      ) : (
        <button onClick={stop} data-testid={`daily-rest-stop-${exIdx}`} className="text-zinc-400 uppercase tracking-wider text-[10px]">Stop</button>
      )}
    </div>
  );
};

// Pre-fills weight from the user's last set; lets them tap "Log set" without leaving the page.
// Each target set gets its own row (Set 1 / Set 2 / Set 3 ...).
const parseTargetReps = (r) => {
  if (typeof r === 'number') return String(r);
  if (!r) return '';
  // "8-12" → "10", "5" → "5", "AMRAP" → ""
  const m = String(r).match(/(\d+)\s*[-–]\s*(\d+)/);
  if (m) return String(Math.round((Number(m[1]) + Number(m[2])) / 2));
  const n = String(r).match(/(\d+)/);
  return n ? n[1] : '';
};

const SetRow = ({ setIdx, exName, defaults, onLogged, exIdx }) => {
  const { toast } = useToast();
  const [weight, setWeight] = useState(defaults.weight);
  const [reps, setReps] = useState(defaults.reps);
  const [rir, setRir] = useState(defaults.rir);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const log = async () => {
    const w = Number(weight);
    const r = Number(reps);
    if ((!w && w !== 0) || !r) {
      toast({ title: 'Fill weight and reps', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await api.addLift({ exercise: exName, weight_kg: w, sets: 1, reps: r, rir: Number(rir) });
      try { await api.streakPing('lift_log'); } catch {}
      setDone(true);
      onLogged && onLogged({ weight: w, reps: r, rir: Number(rir) });
    } catch (err) {
      toast({ title: 'Could not log', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid={`daily-set-row-${exIdx}-${setIdx}`}
      className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 ${
        done ? 'border-[#BEFF38]/50 bg-[#BEFF38]/5' : 'border-[#2a2f3a] bg-[#0b0c10]'
      }`}
    >
      <span className={`shrink-0 w-7 h-7 rounded-md grid place-items-center text-[11px] font-black ${
        done ? 'bg-[#BEFF38]/20 text-[#BEFF38]' : 'bg-zinc-900 text-zinc-400'
      }`}>
        {done ? '✓' : setIdx + 1}
      </span>
      <input
        type="number" step="0.5" inputMode="decimal" placeholder="kg" value={weight}
        onChange={(e) => setWeight(e.target.value)} disabled={done}
        data-testid={`daily-set-kg-${exIdx}-${setIdx}`}
        aria-label={`Set ${setIdx + 1} weight in kilograms`}
        className="min-w-0 flex-1 bg-[#0f1115] border border-[#2a2f3a] focus:border-[#BEFF38] rounded px-2 py-1.5 text-white text-sm outline-none disabled:opacity-60"
      />
      <input
        type="number" min="1" inputMode="numeric" placeholder="reps" value={reps}
        onChange={(e) => setReps(e.target.value)} disabled={done}
        data-testid={`daily-set-reps-${exIdx}-${setIdx}`}
        aria-label={`Set ${setIdx + 1} reps`}
        className="min-w-0 flex-1 bg-[#0f1115] border border-[#2a2f3a] focus:border-[#BEFF38] rounded px-2 py-1.5 text-white text-sm outline-none disabled:opacity-60"
      />
      <input
        type="number" min="0" max="10" inputMode="numeric" placeholder="rir" value={rir}
        onChange={(e) => setRir(e.target.value)} disabled={done}
        data-testid={`daily-set-rir-${exIdx}-${setIdx}`}
        aria-label={`Set ${setIdx + 1} reps in reserve`}
        className="min-w-0 w-12 bg-[#0f1115] border border-[#2a2f3a] focus:border-[#BEFF38] rounded px-2 py-1.5 text-white text-sm outline-none disabled:opacity-60"
      />
      <button
        onClick={log} disabled={busy || done}
        data-testid={`daily-set-log-${exIdx}-${setIdx}`}
        aria-label={`Log set ${setIdx + 1}`}
        className={`shrink-0 h-8 w-8 rounded inline-flex items-center justify-center ${
          done ? 'bg-[#BEFF38]/20 text-[#BEFF38]' : 'bg-[#BEFF38] text-black hover:bg-[#a6e62e] disabled:opacity-50'
        }`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={3} />}
      </button>
    </div>
  );
};

const ExerciseLogRow = ({ ex, idx, plan }) => {
  const targetSets = Math.max(1, parseInt(ex.sets, 10) || 3);
  const [open, setOpen] = useState(false);
  const [extraSets, setExtraSets] = useState(0);
  const [swapOpen, setSwapOpen] = useState(false);
  const [currentName, setCurrentName] = useState(ex.name);  // updated when user picks a swap
  const [defaults, setDefaults] = useState({
    weight: '', reps: parseTargetReps(ex.reps), rir: 2,
  });
  const [last, setLast] = useState(null);
  const [recent, setRecent] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Pull last set + suggested next on first expand
  useEffect(() => {
    if (!open || last !== null) return;
    (async () => {
      try {
        const history = await api.listLifts(currentName);
        const arr = history || [];
        setRecent(arr);
        if (arr.length > 0) {
          setLast(arr[0]);
          setDefaults((d) => ({
            ...d,
            weight: d.weight || String(arr[0].weight_kg ?? ''),
            reps:   d.reps   || String(arr[0].reps ?? ''),
            rir:    arr[0].rir ?? d.rir,
          }));
        } else {
          setLast({});
        }
      } catch { setLast({}); }
    })();
    // eslint-disable-next-line
  }, [open]);

  const onSetLogged = (logged) => {
    setDefaults({ weight: String(logged.weight), reps: String(logged.reps), rir: logged.rir });
    api.listLifts(currentName).then((h) => setRecent(h || [])).catch(() => {});
  };

  const onSwapPick = (alt) => {
    setCurrentName(alt.name);
    // Reset history/defaults for the new exercise
    setLast(null);
    setRecent([]);
    setDefaults({ weight: '', reps: parseTargetReps(ex.reps), rir: 2 });
  };

  const totalSets = targetSets + extraSets;

  return (
    <li className="px-3 py-2.5 bg-[#0b0c10]">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <button
            onClick={() => setOpen(!open)}
            data-testid={`daily-toggle-log-${idx}`}
            className="text-white font-semibold text-sm text-left hover:text-[#ff7a1a]"
          >
            {currentName}
            {currentName !== ex.name && (
              <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#BEFF38] text-black text-[8px] font-black uppercase tracking-wider">
                <Shuffle className="w-2.5 h-2.5" /> Swapped
              </span>
            )}
          </button>
          <a
            href={youtubeVideoUrl(currentName)}
            target="_blank"
            rel="noreferrer"
            data-testid={`daily-watch-demo-${idx}`}
            aria-label={`Watch demo for ${currentName}`}
            className="inline-flex items-center gap-1 text-[10px] font-black text-[#ff7a1a] hover:text-[#ff8a33] uppercase tracking-wider"
          >
            <PlayCircle className="w-3.5 h-3.5" /> Demo
            {hasCuratedVideo(currentName) && (
              <span className="ml-0.5 px-1 rounded-full bg-[#BEFF38] text-black text-[8px]">★</span>
            )}
          </a>
          <button
            onClick={() => setSwapOpen(true)}
            data-testid={`daily-swap-${idx}`}
            aria-label={`Swap ${currentName}`}
            className="inline-flex items-center gap-1 text-[10px] font-black text-zinc-400 hover:text-[#BEFF38] uppercase tracking-wider"
          >
            <Shuffle className="w-3.5 h-3.5" /> Swap
          </button>
        </div>
        <span className="text-[#ff7a1a] text-xs font-bold whitespace-nowrap">{ex.sets} × {ex.reps}</span>
      </div>
      {ex.notes && <p className="text-[#aab1bf] text-xs mt-1 leading-relaxed">{ex.notes}</p>}

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid={`daily-open-log-${idx}`}
          className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#BEFF38] hover:text-[#a6e62e]"
        >
          <Plus className="w-3 h-3" /> Log sets
        </button>
      ) : (
        <div className="mt-2 space-y-1.5">
          {last?.weight_kg !== undefined && (
            <p className="text-[10px] text-zinc-500">
              Last session: {last.weight_kg}kg × {last.reps} × {last.sets} @ RIR {last.rir}
            </p>
          )}
          <div className="flex items-center gap-2 text-[10px] text-zinc-500 uppercase tracking-wider font-bold px-2">
            <span className="w-7 text-center">Set</span>
            <span className="flex-1">kg</span>
            <span className="flex-1">reps</span>
            <span className="w-12">rir</span>
            <span className="w-8" />
          </div>
          {Array.from({ length: totalSets }, (_, i) => (
            <SetRow
              key={`${currentName}-${i}`}
              setIdx={i}
              exIdx={idx}
              exName={currentName}
              defaults={defaults}
              onLogged={onSetLogged}
            />
          ))}
          <RestTimer exIdx={idx} />
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => setExtraSets((n) => n + 1)}
              data-testid={`daily-add-set-${idx}`}
              className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg border border-[#2a2f3a] text-zinc-300 text-[10px] font-bold uppercase tracking-wider hover:border-[#BEFF38]"
            >
              <Plus className="w-3.5 h-3.5" /> Add set
            </button>
            <button
              onClick={() => setShowHistory(!showHistory)}
              data-testid={`daily-log-history-${idx}`}
              className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg border border-[#2a2f3a] text-zinc-300 text-[10px] font-bold uppercase tracking-wider hover:border-[#BEFF38]"
            >
              <History className="w-3.5 h-3.5" /> History
            </button>
          </div>
          {showHistory && recent.length > 0 && (
            <ul className="space-y-1 max-h-32 overflow-y-auto pr-1">
              {recent.slice(0, 8).map((h) => (
                <li key={h.id} className="text-[11px] flex justify-between bg-[#0b0c10] border border-[#1f2937] rounded px-2 py-1">
                  <span className="text-white font-semibold">{h.weight_kg}kg × {h.reps} × {h.sets}</span>
                  <span className="text-zinc-500">RIR {h.rir} · {new Date(h.created_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
          {showHistory && recent.length === 0 && (
            <p className="text-[11px] text-zinc-500 italic">No history yet for this exercise.</p>
          )}
        </div>
      )}

      {swapOpen && (
        <SwapSheet
          exerciseName={currentName}
          userEquipment={plan?.equipment || []}
          userInjuries={plan?.injuries || []}
          onPick={onSwapPick}
          onClose={() => setSwapOpen(false)}
        />
      )}
    </li>
  );
};

const DailyCallCard = ({ call, plan, onGenerate, generating, canGenerate }) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [injuryReview, setInjuryReview] = useState(null); // { due, injuries, days_since }
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewedInjuries, setReviewedInjuries] = useState(null); // local override after a review

  // Ask the backend whether it's time to nudge the user about their injury
  // chips. We only check once per mount and only when the user has a plan —
  // no point asking new users.
  useEffect(() => {
    if (!plan) return;
    api.injuryReviewStatus()
      .then((s) => setInjuryReview(s))
      .catch(() => {}); // soft-fail, banner just won't appear
  }, [plan]);

  const reviewDue = injuryReview?.due && (reviewedInjuries == null);
  const reviewInjuries = reviewedInjuries ?? injuryReview?.injuries ?? [];

  return (
  <Card
    title="Today's call" icon={Sparkles}
    action={(
      <Btn onClick={onGenerate} disabled={!canGenerate || generating}>
        {generating && <Loader2 className="w-4 h-4 animate-spin" />}
        {call ? 'Regenerate' : 'Generate'}
      </Btn>
    )}
  >
    {!call ? (
      <div className="space-y-4">
        <ShojinStrip />
        <ContextualNudgeBanner />
        <Empty>No session yet — tap Generate and we'll build one around today's window.</Empty>
      </div>
    ) : (
      <div className="space-y-4">
        {/* Shōjin Dō — identity anchor, comeback banner, show-up protocol */}
        <ShojinStrip />

        {/* Fuel & recovery — context-aware nudge when a pattern is detected */}
        <ContextualNudgeBanner />

        {/* Periodic injury review nudge — soft, dismissable, never automatic */}
        {reviewDue && reviewInjuries.length > 0 && (
          <button
            type="button"
            onClick={() => setReviewOpen(true)}
            data-testid="injury-review-banner"
            className="w-full text-left rounded-lg border border-[#BEFF38]/30 bg-[#BEFF38]/5 hover:bg-[#BEFF38]/10 transition-colors p-3"
          >
            <p className="text-[#BEFF38] font-bold text-[10px] uppercase tracking-wide flex items-center gap-1.5">
              <Heart className="w-3.5 h-3.5" /> Quick check-in
            </p>
            <p className="text-white text-xs mt-1 leading-snug">
              Still working around your{' '}
              <span className="font-bold">{reviewInjuries.join(' + ')}</span>?
            </p>
            <p className="text-zinc-500 text-[10px] mt-0.5">
              Tap to review — anything healed will rejoin your library.
            </p>
          </button>
        )}

        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] font-black text-[#BEFF38]">Focus</p>
          <p className="text-white text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{call.focus}</p>
          <p className="text-zinc-300 text-sm mt-1 leading-relaxed">{call.training}</p>
        </div>

        {Array.isArray(call.exercises) && call.exercises.length > 0 && (
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] font-black text-[#BEFF38] mb-2">Today&apos;s exercises</p>
            <ul className="divide-y divide-[#1f2937] border border-[#1f2937] rounded-lg overflow-hidden">
              {call.exercises.map((ex, i) => (
                <ExerciseLogRow key={i} ex={ex} idx={i} plan={plan} />
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-zinc-800 p-3 bg-zinc-950/60">
            <p className="flex items-center gap-1.5 text-[#BEFF38] text-[10px] font-mono uppercase tracking-[0.18em] font-black"><Flame className="w-3.5 h-3.5" /> Calories</p>
            <p className="text-zinc-200 text-sm font-bold mt-1">{call.calories} kcal</p>
          </div>
          <div className="rounded-lg border border-zinc-800 p-3 bg-zinc-950/60">
            <p className="flex items-center gap-1.5 text-[#BEFF38] text-[10px] font-mono uppercase tracking-[0.18em] font-black"><Droplet className="w-3.5 h-3.5" /> Hydration</p>
            <p className="text-zinc-200 text-sm font-bold mt-1">{call.hydration_ml} ml</p>
          </div>
          <div className="rounded-lg border border-zinc-800 p-3 bg-zinc-950/60">
            <p className="flex items-center gap-1.5 text-[#BEFF38] text-[10px] font-mono uppercase tracking-[0.18em] font-black"><Heart className="w-3.5 h-3.5" /> Recovery</p>
            <p className="text-zinc-400 text-xs mt-1 leading-snug">{call.recovery}</p>
          </div>
        </div>

        {Array.isArray(call.avoided) && call.avoided.length > 0 && (
          <SafetyAdaptedPanel items={call.avoided} />
        )}

        {call.message && (
          <p className="text-[#cbb39a] italic text-sm border-l-2 border-[#ff7a1a] pl-3">{call.message}</p>
        )}

        <button
          onClick={() => setSheetOpen(true)}
          data-testid="mark-workout-complete-btn"
          className="w-full mt-2 rounded-full h-11 bg-[#BEFF38] hover:bg-[#a8e62e] text-black font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 shadow-[0_4px_20px_-4px_rgba(190,255,56,0.4)] transition-colors"
        >
          <Check className="w-4 h-4" strokeWidth={3} /> Mark workout complete
        </button>
      </div>
    )}
    <CompleteWorkoutSheet
      open={sheetOpen}
      onClose={() => setSheetOpen(false)}
      source="daily_call"
      title={call?.focus || "Today's call"}
      exercises={call?.exercises || []}
    />
    {reviewOpen && (
      <InjuryReviewSheet
        injuries={reviewInjuries}
        daysSince={injuryReview?.days_since || 14}
        onClose={() => setReviewOpen(false)}
        onUpdated={(res) => {
          // Update local state so the banner disappears immediately and the
          // page's plan cache (used by SwapSheet etc.) reflects the new list.
          setReviewedInjuries(res?.injuries || []);
          try {
            const cached = JSON.parse(localStorage.getItem('fff_plan_cache') || '{}');
            cached.injuries = res?.injuries || [];
            localStorage.setItem('fff_plan_cache', JSON.stringify(cached));
          } catch { /* ignore */ }
        }}
      />
    )}
  </Card>
  );
};

export default DailyCallCard;
